/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Workspace State Management Store (Zustand)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export interface WorkspaceSummary {
  id: string;
  name: string;
  slug: string;
  role?: 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER' | string;
  avatarUrl?: string | null;
  type?: 'personal' | 'team';
}

interface WorkspaceState {
  activeWorkspaceId: string | null;
  workspaces: WorkspaceSummary[];
  isLoading: boolean;
  setActiveWorkspaceId: (id: string) => void;
  setWorkspaces: (workspaces: WorkspaceSummary[]) => void;
  createWorkspace: (workspace: WorkspaceSummary) => void;
  setLoading: (loading: boolean) => void;
  getActiveWorkspace: () => WorkspaceSummary | undefined;
}

const STORAGE_KEY = 'sophia_active_workspace_id';

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  activeWorkspaceId: typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null,
  workspaces: [],
  isLoading: false,

  setActiveWorkspaceId: (id: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, id);
    }
    set({ activeWorkspaceId: id });
  },

  setWorkspaces: (workspaces: WorkspaceSummary[]) => {
    const currentId = get().activeWorkspaceId;
    let nextId = currentId;

    // Default to the first workspace if none is currently active or active not in list
    if (workspaces.length > 0 && (!currentId || !workspaces.some((w) => w.id === currentId))) {
      nextId = workspaces[0].id;
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, nextId);
      }
    }

    set({ workspaces, activeWorkspaceId: nextId });
  },

  createWorkspace: (newWs: WorkspaceSummary) => {
    const { workspaces } = get();
    const updated = [...workspaces, newWs];
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, newWs.id);
    }
    set({ workspaces: updated, activeWorkspaceId: newWs.id });
  },

  setLoading: (isLoading: boolean) => set({ isLoading }),

  getActiveWorkspace: () => {
    const { workspaces, activeWorkspaceId } = get();
    return workspaces.find((w) => w.id === activeWorkspaceId);
  },
}));
